<template>
  <div
    class="product-item"
    :class="{ 'product-item--adding': addingProductToCart, 'product-item--unavailable': !availableToPurchase }"
  >
    <div class="product-item__image-wrapper relative">
      <a
        :href="url"
        class="product-item__image"
        :class="{ 'product-item__image--jpg': isJpgImage, 'product-item__image-no-hover': !hasHoverImage }"
      >
        <img
          v-if="lazyLoad"
          :key="`${product.handle}-item-main-image`"
          v-lazy="mainImage"
          :alt="`${product.title}-main-image`"
          :width="imageWidth"
          :height="imageWidth"
        >
        <img
          v-else
          :src="mainImage"
          :alt="`${product.title}-main-image`"
          :width="imageWidth"
          :height="imageWidth"
        >

        <div
          v-if="hasHoverImage"
          class="product-item__image--hover"
        >
          <img
            :key="`${product.handle}-item-hover-image`"
            v-lazy="hoverImage"
            :alt="`${product.title}-hover-image`"
            :width="hoverImageWidth"
            :height="hoverImageWidth"
          >
        </div>

        <div
          v-if="!isSearchResult"
          class="product-item__elite"
        >
          <EliteIcon />
          {{ pointsValue }}
        </div>
      </a>
    </div>

    <div class="product-item__information">
      <h2
        v-if="collectionTemplate"
        class="product-item__title"
      >
        <a :href="url">
          {{ product.title }}
        </a>
      </h2>
      <h3
        v-else
        class="product-item__title"
      >
        <a :href="url">
          {{ product.title }}
        </a>
      </h3>

      <p
        v-if="product.subtitle && product.subtitle.length > 0"
        class="product-item__text"
      >
        {{ product.subtitle }}
      </p>

      <div class="product-item__price-line">
        <p class="product-item__price">
          <span v-if="getOptions.length && !isCombinedProduct">From</span>
          {{ price }}
          <s v-if="compareAtPrice">
            {{ compareAtPrice }}
          </s>
        </p>
        <YotpoStars :product="yotpoProduct" />
      </div>

      <div
        v-if="offerInformation"
        class="offer-information"
        v-html="offerInformation"
      />

      <div
        v-if="hasUsps"
        class="product-item__usps"
      >
        <ul>
          <li
            v-for="usp in product.metafields.usps"
            :key="`${product.handle}-usp-${usp.title}`"
            class="product-item__usp"
          >
            <div
              v-if="usp.icon"
              class="product-item__usp-icon"
            >
              <img
                :src="usp.icon"
                :alt="usp.title"
                width="20"
                height="20"
              >
            </div>
            <div
              v-else
              v-html="usp.icon_svg_code"
            />
            {{ usp.title }}
          </li>
        </ul>
      </div>

      <template v-if="showQuickView">
        <button
          class="product-item__quick-view-cta btn"
          @click="setQuickViewProduct(product)"
        >
          {{ strings.quickView }}
        </button>
      </template>
      <template v-else-if="isCombinedProduct ||isSomeBundle || isComingSoon || !product.available">
        <a
          :href="url"
          class="product-item__quick-view-cta btn"
        >
          {{ strings.viewDetails }}
        </a>
      </template>
      <template v-else-if="isPreOrder">
        <button
          class="product-item__quick-view-cta btn"
          @click="addToCart"
        >
          {{ strings.preOrder }}
        </button>
      </template>
      <template v-else-if="product.available">
        <button
          class="product-item__quick-view-cta btn"
          @click="addToCart"
        >
          {{ strings.addToCartWithoutPrice }}
        </button>
      </template>
    </div>

    <div
      v-if="highlightedProduct"
      class="product-item__elite-badge absolute top-0"
    >
      {{ pointsValue }}
    </div>
    <div
      v-if="!product.available"
      class="product-sale-badge product-sale-badge--sold-out"
    >
      <template v-if="isComingSoon">
        {{ strings.comingSoon }}
      </template>
      <template v-else>
        {{ strings.soldOut }}
      </template>
    </div>
    <div
      v-else-if="product.tags.includes('promo-badge:New In')"
      class="product-sale-badge product-sale-badge--sold-out"
    >
      {{ saleBadge }}
    </div>
    <div
      v-else-if="saleBadge"
      class="product-sale-badge"
    >
      {{ saleBadge }}
    </div>
  </div>
</template>

<script>
import {useCartStore} from '../pinia/CartStore'
import {useCollectionStore} from '../pinia/CollectionStore'
import {mapActions} from 'pinia'

import {productPointsValue, productPrice} from '../helpers/product'
import {getSizedImageUrl} from '@shopify/theme-images'

import YotpoStars from '../components/product-yotpo-stars'

import EliteIcon from '../icons/elite-icon'

export default {
  components: {
    EliteIcon,
    YotpoStars
  },
  props: {
    isSearchResult: {
      type: Boolean,
      default: false
    },
    collectionTemplate: {
      type: Boolean,
      default: false
    },
    highlightedProduct: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: 99
    },
    product: {
      type: Object,
      default: null
    },
    trackingUrl: {
      type: String,
      default: null
    },
    recommendations: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      global: {
        productTags: {...window.theme.productTags}
      },
      addFormShowing: false,
      addingProductToCart: false,
      strings: {...window.theme.strings.product},
      flavourCollections: [...window.theme.flavourCollections],
    }
  },
  computed: {
    yotpoProduct () {
      return (this.isCombinedProduct  && !this.recommendations) ? this.defaultVariant.product : this.product
    },
    showCollectionHighlight() {
      return this.collectionTemplate && this.index === 3
    },
    isMetafieldsBundle() {
      return this.product?.metafieldsBundle?.enabled
    },
    isCustomisableBundle() {
      return !!window.theme.bundles.find(bundle => bundle.product.handle === this.product.handle)
    },
    isBundleDummy() {
      return this.product.tags.includes('bundle-dummy')
    },
    matchingBundle() {
      if (this.isCustomisableBundle) {
        return window.theme.bundles.find(bundle => bundle.product.handle === this.product.handle)
      }
      return null
    },
    lazyLoad() {
      return this.index > 3
    },
    url() {
      return this.trackingUrl || `/products/${this.product.handle}`
    },
    isJpgImage() {
      return (this.product.image && this.product.image?.includes('.jpg')) || (this.product.images && this.product.images?.[0]?.includes('.jpg')) || (this.product.image && this.product.image?.includes('.gif')) || (this.product.images && this.product.images?.[0]?.includes('.gif'))
    },
    priceUnformatted() {
      if (this.getOptions.length) {
        return this.getOptions
          .map(simple => simple?.variants?.[0]?.price || simple?.price)
          .sort()[0]
      }
      if (this.isCustomisableBundle) {
        if (this.matchingBundle) {
          return this.matchingBundle.price
        } else {
          return ''
        }
      } else {
        return this.product.price
      }
    },
    price() {
      return productPrice(this.priceUnformatted)
    },
    compareAtPrice() {
      return this.product.compare_at_price && this.product.compare_at_price > this.priceUnformatted ? productPrice(this.product.compare_at_price) : null
    },
    pointsValue() {
      if (this.matchingBundle) {
        const price = (this.matchingBundle.baseCountryPrice === undefined || this.matchingBundle.baseCountryPrice == 0) ? this.matchingBundle.price : this.matchingBundle.baseCountryPrice
        return productPointsValue(price)
      } else {
        const price = (this.product.baseCountryPrice === undefined || this.product.baseCountryPrice == 0) ? this.product.price : this.product.baseCountryPrice
        return productPointsValue(price)
      }
    },
    offerInformation () {
      if (this.matchingBundle) {
        return this.matchingBundle.offerInformation
      } else {
        return this.product.offerInformation
      }
    },
    imageWidth() {
      let imageWidth = 235.5
      if (this.highlightedProduct) {
        imageWidth = 1200
      } else if (this.isJpgImage) {
        imageWidth = 325
      }
      return imageWidth
    },
    mainImage() {
      if (this.product.image) {
        return getSizedImageUrl(this.product.image, `${this.imageWidth * 2}x`)
      }
      if (this.product.images?.[0]) {
        return getSizedImageUrl(this.product.images[0], `${this.imageWidth * 2}x`)
      }
      return null
    },
    hasGradientImage() {
      return (this.product.tags.includes(this.global.productTags.energyTub400gTag) ||
          this.product.tags.includes(this.global.productTags.energyTub150gTag) ||
          this.product.tags.includes(this.global.productTags.hydrateTubTag) ||
          this.product.tags.includes(this.global.productTags.sachetTag)) &&
        (this.product.image && this.product.image?.includes('.jpg')) || (this.product.images && this.product.images?.[0]?.includes('.jpg'))
    },
    hasHoverImage() {
      if (!this.hasGradientImage || !this.hoverImageWidth) {
        return false
      }
      const imageArrayLength = this.hasGradientImage ? 3 : 2
      return this.product.images && this.product.images.length >= imageArrayLength && !this.highlightedProduct
    },
    hoverImageWidth() {
      return this.imageWidth
    },
    hoverImage() {
      if (this.hasHoverImage) {
        if (this.product.images?.[2]) {
          return getSizedImageUrl(this.product.images[2], `${this.hoverImageWidth * 2}x`)
        }
        if (this.product.images?.[1]) {
          return getSizedImageUrl(this.product.images[1], `${this.hoverImageWidth * 2}x`)
        }
      }
      return null
    },
    saleBadge() {
      let saleBadge = null

      this.product.tags.forEach(tag => {
        if (tag.includes('promo-badge:')) {
          saleBadge = tag.split('promo-badge:')[1]
          return
        }
      })
      return saleBadge
    },

    flavourCollectionHandle() {
      const flavourTag = this.product.tags.find(tag => tag.includes('flavour:'))
      if (flavourTag) {
        return flavourTag.replace('flavour:', '')
      }
      return null
    },
    flavourCollection () {
      if (this.flavourCollectionHandle) {
        return this.flavourCollections.find(collection => collection.handle === this.flavourCollectionHandle)
      }
      return null
    },

    hasVariants() {
      return this.product.variants.length > 1
    },
    getOptions() {
      let simples = []
      if (this.flavourCollection && this.flavourCollection.products.length > 1) {
          simples = this.flavourCollection.products
      } else if (this.hasVariants) {
          simples = this.product.variants
      }
      return simples
    },
    enableAddForm() {
      return !this.highlightedProduct && (this.hasVariants || (this.flavourCollectionHandle && this.product.flavourCollectionSize > 1) || this.matchingBundle !== null)
    },

    defaultVariant() {
      return this.product.variants[0] || this.product
    },
    soldOut() {
      return this.isCombinedProduct ? !this.product.available :!this.defaultVariant.available
    },
    availableToPurchase() {
      return !this.soldOut
    },

    hasUsps() {
      return this.product.metafields?.usps?.length > 0
    },

    isCombinedProduct() {
      return this.product.tags.includes('combined')
    },
    isPreOrder() {
      return this.product.tags.includes('preorder') && this.product.available
    },
    isComingSoon() {
      return this.product.tags.includes('coming-soon') && !this.product.available
    },
    isSomeBundle () {
      return this.matchingBundle || this.isMetafieldsBundle || this.isBundleDummy
    },
    showQuickView() {
      return this.isCombinedProduct && !this.recommendations && this.product.variants.length > 1
    },


    payload() {
      return {
        items: [
          {
            id: this.defaultVariant.id,
            quantity: 1
          }
        ]
      }
    }
  },
  methods: {
    ...mapActions(useCartStore, [
      'addItems',
    ]),
    ...mapActions(useCollectionStore, [
      'setQuickViewProduct',
    ]),

    // addToCartTrigger() {
    //   if (this.enableAddForm) {
    //     this.openAddForm()
    //   } else {
    //     this.addToCart()
    //   }
    // },
    // openAddForm() {
    //   this.addFormShowing = true
    // },
    closeAddForm() {
      this.addFormShowing = false
    },
    async addToCart() {
      if (this.availableToPurchase) {
        this.addingProductToCart = true
        if (this.isBundle) {
          await this.addItems(this.bundlePayload)
        } else if (this.product?.metafieldsBundle?.enabled) {
          await this.addItems(this.bundleBundleItemPayload(this.product))
        } else {
          await this.addItems(this.payload)
        }
        this.addingProductToCart = false
      }
    },
    bundleBundleItemPayload(bundle) {

      bundle.metafieldsBundle.bundle_options.forEach((option, index) => {
        const availableProducts = option.products.filter(product => product.available)
        const initialProducts = []
        while (initialProducts.length < option.minimumQuantity) {
          availableProducts.forEach(product => {
            if (initialProducts.length < option.minimumQuantity) {
              initialProducts.push(product)
            }
          })
        }
        bundle.metafieldsBundle.bundle_options[index].initialProducts = [...initialProducts]
        bundle.metafieldsBundle.bundle_options[index].selectedProducts = [...initialProducts]
      })

      const isDuo = () => {
        return (bundle.metafieldsBundle.bundle_options.length === 1 && bundle.metafieldsBundle.bundle_options[0].minimumQuantity === 2 && bundle.metafieldsBundle.bundle_options[0].maximumQuantity === 2) ||
            (bundle.metafieldsBundle.bundle_options.length === 2 && bundle.metafieldsBundle.bundle_options[0].maximumQuantity === 1 && bundle.metafieldsBundle.bundle_options[0].productType === 'Tub' && bundle.metafieldsBundle.bundle_options[1].maximumQuantity === 1 && bundle.metafieldsBundle.bundle_options[1].productType === 'Tub')
      }

      const bundleKey = () => {
        const str = bundle.metafieldsBundle.bundle_options.map((option, index) => {
          return option.initialProducts.map(product => {
            return `${product.variantId}-${product.quantity}`
          }).join('-')
        }).join('-')

        let hash = 0
        for (let i = 0; i < str.length; i++) {
          hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0
        }
        return (hash >>> 0).toString(36)
      }

      const payload = {
        items: [
          {
            id: bundle.variantId ?? bundle.variants[0].id,
            quantity: 1,
            properties: {
              _bundle_parent: true,
              _bundle_key: bundleKey(),
            }
          }
        ]
      }

      bundle.metafieldsBundle.bundle_options.forEach((collection, collectionIndex) => {
        collection.selectedProducts.forEach((product, i) => {
          /*
            If duo of two different type of tubs
            update index of second collection item
          */
          if (isDuo && bundle.metafieldsBundle.bundle_options.length === 2 && collectionIndex === 1) {
            i = 1
          }
          payload.items[0].properties['_' + `${collection.productType ?? collection.label} ${i + 1}`] = product.handle
          if (!payload.items[0].properties['_bundle_options']?.length) {
            payload.items[0].properties['_bundle_options'] = []
          }
          payload.items[0].properties['_bundle_options'] = [...payload.items[0].properties['_bundle_options'], product.variantId ?? product.variants[0].id]
        })
      })

      return payload
    },
  }
}
</script>
